import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import Card from '../../../components/ui/Card'
import Button from '../../../components/ui/Button'
import SearchableSelect from '../../../components/ui/SearchableSelect'
import TypeaheadSearch, { TypeaheadOption } from '../../../components/ui/TypeaheadSearch'
import Badge from '../../../components/ui/Badge'
import Table, { Pagination } from '../../../components/ui/Table'
import api from '../../../lib/api'
import { formatCurrency, formatDate } from '../../../lib/formatters'
import type { Invoice, PaginatedResponse, InvoiceStatus } from '../../../types'
import toast from 'react-hot-toast'

const STATUS_OPTIONS: { value: InvoiceStatus | ''; label: string }[] = [
  { value: '', label: 'All Status' },
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'partial', label: 'Partial' },
  { value: 'paid', label: 'Paid' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'cancelled', label: 'Cancelled' },
]

export default function InvoicesListPage() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['invoices', page, search, status],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: '10' })
      if (search) params.append('search', search)
      if (status) params.append('status', status)
      const { data } = await api.get<PaginatedResponse<Invoice>>(`/invoices?${params}`)
      return data
    },
  })

  const getStatusBadge = (invoiceStatus: InvoiceStatus) => {
    const variants: Record<InvoiceStatus, 'success' | 'warning' | 'error' | 'info' | 'gray'> = {
      draft: 'gray',
      sent: 'info',
      partial: 'warning',
      paid: 'success',
      overdue: 'error',
      cancelled: 'gray',
    }
    return <Badge variant={variants[invoiceStatus]}>{invoiceStatus}</Badge>
  }

  const columns = [
    {
      key: 'displayId',
      header: 'Invoice #',
      render: (invoice: Invoice) => (
        <Link to={`/invoices/${invoice.id}`} className="text-primary-600 hover:text-primary-700 font-medium">
          {invoice.displayId}
        </Link>
      ),
    },
    {
      key: 'doctor',
      header: 'Doctor / Clinic',
      render: (invoice: Invoice) => (
        <div>
          <p className="font-medium text-gray-900 dark:text-white">{invoice.doctor?.name ?? '-'}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{invoice.doctor?.clinicName}</p>
        </div>
      ),
    },
    {
      key: 'issueDate',
      header: 'Issue Date',
      render: (invoice: Invoice) => (
        <span className="text-sm text-gray-600 dark:text-gray-300">
          {formatDate(invoice.issueDate)}
        </span>
      ),
    },
    {
      key: 'dueDate',
      header: 'Due Date',
      render: (invoice: Invoice) => {
        const isOverdue = new Date(invoice.dueDate) < new Date() &&
          !['paid', 'cancelled'].includes(invoice.status)
        return (
          <span className={`text-sm ${isOverdue ? 'text-red-600 font-medium' : 'text-gray-600 dark:text-gray-300'}`}>
            {formatDate(invoice.dueDate)}
          </span>
        )
      },
    },
    {
      key: 'totalAmount',
      header: 'Total',
      render: (invoice: Invoice) => (
        <span className="font-medium text-gray-900 dark:text-white">
          {formatCurrency(invoice.totalAmount)}
        </span>
      ),
    },
    {
      key: 'balanceAmount',
      header: 'Balance',
      render: (invoice: Invoice) => (
        <span className={`font-medium ${invoice.balanceAmount > 0 ? 'text-red-600' : 'text-green-600'}`}>
          {formatCurrency(invoice.balanceAmount)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (invoice: Invoice) => getStatusBadge(invoice.status),
    },
    {
      key: 'actions',
      header: '',
      render: (invoice: Invoice) => (
        <div className="flex justify-end gap-2">
          <Link
            to={`/invoices/${invoice.id}`}
            className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
            onClick={(e) => e.stopPropagation()}
          >
            <i className="fas fa-eye"></i>
          </Link>
          <button
            className="p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
            onClick={async (e) => {
              e.stopPropagation()
              try {
                const response = await api.get(`/invoices/${invoice.id}/pdf`, {
                  responseType: 'blob',
                })
                const blob = new Blob([response.data], { type: 'application/pdf' })
                const url = window.URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                link.download = `${invoice.displayId}.pdf`
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                window.URL.revokeObjectURL(url)
                toast.success('PDF downloaded')
              } catch {
                toast.error('Failed to download PDF')
              }
            }}
          >
            <i className="fas fa-download"></i>
          </button>
        </div>
      ),
    },
  ]

  const totalOutstanding = data?.data?.reduce((sum, inv) => sum + inv.balanceAmount, 0) ?? 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Invoices</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage GST-compliant invoices
          </p>
        </div>
        <Link to="/invoices/new">
          <Button>
            <i className="fas fa-plus mr-2"></i>
            Create Invoice
          </Button>
        </Link>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <p className="text-blue-100 text-sm">Total Invoices</p>
          <p className="text-2xl font-bold mt-1">{data?.total ?? 0}</p>
        </Card>
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <p className="text-green-100 text-sm">Paid</p>
          <p className="text-2xl font-bold mt-1">
            {data?.data?.filter(i => i.status === 'paid').length ?? 0}
          </p>
        </Card>
        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <p className="text-amber-100 text-sm">Pending</p>
          <p className="text-2xl font-bold mt-1">
            {data?.data?.filter(i => ['sent', 'partial'].includes(i.status)).length ?? 0}
          </p>
        </Card>
        <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
          <p className="text-red-100 text-sm">Outstanding</p>
          <p className="text-2xl font-bold mt-1">{formatCurrency(totalOutstanding)}</p>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <TypeaheadSearch
              placeholder="Search by invoice #, doctor..."
              value={search}
              onChange={(value) => { setSearch(value); setPage(1) }}
              onSelect={(option) => { 
                window.location.href = `/invoices/${option.id}`
              }}
              queryKey={['invoices', 'search']}
              queryFn={async () => {
                try {
                  const { data } = await api.get<PaginatedResponse<Invoice>>('/invoices?limit=100')
                  return (data.data || []).map((invoice: Invoice): TypeaheadOption => ({
                    id: invoice.id,
                    label: invoice.displayId,
                    sublabel: `${invoice.doctor?.name || 'Unknown'} • ${formatCurrency(invoice.totalAmount)}`,
                    icon: 'fa-file-invoice',
                  }))
                } catch {
                  return []
                }
              }}
              maxResults={8}
            />
          </div>
          <SearchableSelect
            value={status}
            onChange={(val) => { setStatus(val); setPage(1) }}
            placeholder="All Status"
            searchable={false}
            options={STATUS_OPTIONS.map(opt => ({ value: opt.value, label: opt.label }))}
          />
        </div>
      </Card>

      {/* Table */}
      <Card padding="none">
        <Table
          columns={columns}
          data={data?.data ?? []}
          loading={isLoading}
          keyExtractor={(invoice) => invoice.id}
          emptyMessage="No invoices found"
          onRowClick={(invoice) => window.location.href = `/invoices/${invoice.id}`}
        />
        {data && data.totalPages > 1 && (
          <Pagination
            page={page}
            totalPages={data.totalPages}
            onPageChange={setPage}
          />
        )}
      </Card>
    </div>
  )
}
